<template>
    <section v-if="ratingIntelligence" class="rating-intel">
        <h2 class="rating-intel__heading">
            <button
                type="button"
                class="rating-intel__header"
                :aria-expanded="isOpen"
                aria-controls="rating-intel-content"
                @click="isOpen = !isOpen"
            >
                <span id="rating-intel-title" class="rating-intel__title">{{ ratingIntelligence.title }}</span>
                <i class="mdi" :class="isOpen ? 'mdi-chevron-down' : 'mdi-chevron-right'" aria-hidden="true"></i>
            </button>
        </h2>

        <BondV2SignupBlurGate
            v-if="isOpen"
            id="rating-intel-content"
            :message="ratingIntelGateMessage"
            @signup-required="handleSignupGate"
        >
            <div class="rating-intel__body">
                <p v-if="ratingIntelligence.description" class="rating-intel__description">
                    {{ ratingIntelligence.description }}
                </p>

            <div v-if="ratingIntelligence.chips.length" class="rating-signals" role="list" aria-label="Credit rating signals">
                <span
                    v-for="(chip, index) in ratingIntelligence.chips"
                    :key="chip"
                    class="rating-signal"
                    :class="ratingSignalClass(chip, index)"
                    role="listitem"
                >
                    {{ chip }}
                </span>
            </div>

            <div v-if="hasRatingSummary || data.creditRiskMeter" class="rating-summary-container">
                <!-- If CreditRiskMeter is present, render it natively inside the intelligence section -->
                <div v-if="data.creditRiskMeter" class="rating-crom-section w-100 mb-6">
                    <CreditRiskMeter :meter="data.creditRiskMeter" variant="full" :security-label="computedSecurityLabel" />
                </div>

                <div v-if="hasRatingSummary" class="rating-summary" :class="{ 'mt-4': data.creditRiskMeter }">
                    <article v-if="ratingIntelligence.currentRating && !data.creditRiskMeter" class="rating-card rating-card--current" aria-label="Current rating">
                        <img
                            v-if="currentRatingIcon"
                            class="rating-card__rating-icon"
                            :src="currentRatingIcon"
                            alt=""
                            aria-hidden="true"
                        />
                        <strong>{{ ratingIntelligence.currentRating }}</strong>
                        <span v-if="ratingIntelligence.currentRatingStatus">{{ ratingIntelligence.currentRatingStatus }}</span>
                    </article>

                    <article v-if="ratingIntelligence.rationaleSummary || ratingIntelligence.summaryPoints" class="rating-card rating-card--rationale">
                        <template v-if="ratingIntelligence.rationaleSummary">
                            <h3 class="rating-card__title">{{ ratingIntelligence.rationaleSummary.label }}</h3>
                            <p>{{ ratingIntelligence.rationaleSummary.value }}</p>
                        </template>
                        <template v-if="ratingIntelligence.summaryPoints?.items.length">
                            <div v-if="ratingIntelligence.summaryPoints.label" class="rating-card__eyebrow">{{ ratingIntelligence.summaryPoints.label }}</div>
                            <ul class="rating-bullets" aria-label="Rating rationale summary points">
                                <li v-for="point in ratingIntelligence.summaryPoints.items" :key="point">{{ point }}</li>
                            </ul>
                        </template>
                    </article>
                </div>
            </div>

            <article v-if="hasRatingTimeline" class="timeline-card">
                <div class="section-copy">
                    <h3 v-if="ratingIntelligence.timeline.label" class="section-title">{{ ratingIntelligence.timeline.label }}</h3>
                </div>

                <div class="timeline-chart" role="img" :aria-label="ratingTimelineAriaLabel">
                    <canvas ref="timelineChartCanvas" role="img" :aria-label="ratingTimelineAriaLabel">
                        {{ ratingTimelineAriaLabel }}
                    </canvas>
                </div>

                <div class="timeline-notes">
                    <article
                        v-for="item in ratingHistory"
                        :key="`${item.year}-${item.title}`"
                        class="timeline-note"
                        :class="`timeline-note--${item.tone}`"
                    >
                        <strong>{{ item.title }}</strong>
                        <p>{{ item.description }}</p>
                    </article>
                </div>
            </article>

            <article v-if="ratingIntelligence.outlook" class="outlook-card">
                <h3 v-if="ratingIntelligence.outlook.label" class="section-title">{{ ratingIntelligence.outlook.label }}</h3>
                <p v-if="ratingIntelligence.outlook.value">
                    {{ ratingIntelligence.outlook.value }}
                </p>
                <template v-if="ratingIntelligence.outlook.implications.length">
                    <div v-if="ratingIntelligence.outlook.implicationsLabel" class="outlook-card__label">{{ ratingIntelligence.outlook.implicationsLabel }}</div>
                    <ul class="outlook-card__bullets">
                        <li v-for="point in ratingIntelligence.outlook.implications" :key="point">{{ point }}</li>
                    </ul>
                </template>
                <strong v-if="ratingIntelligence.outlook.watchFor">
                    <template v-if="ratingIntelligence.outlook.watchForLabel">{{ ratingIntelligence.outlook.watchForLabel }}: </template>{{ ratingIntelligence.outlook.watchFor }}
                </strong>
            </article>

            <article v-if="ratingIntelligence.drivers?.items.length" class="drivers-card">
                <h3 v-if="ratingIntelligence.drivers.label" class="section-title">{{ ratingIntelligence.drivers.label }}</h3>
                <div class="drivers-grid">
                    <article v-for="driver in ratingIntelligence.drivers.items" :key="driver.title" class="driver-card">
                        <div class="driver-card__header">
                            <h4 class="driver-card__title">{{ driver.title }}</h4>
                            <span :class="['driver-card__status', `driver-card__status--${driver.tone}`]">{{ driver.status }}</span>
                        </div>
                        <p>{{ driver.description }}</p>
                    </article>
                </div>
            </article>

            <article v-if="ratingIntelligence.peerComparison" class="peer-card">
                <div class="section-copy">
                    <h3 class="section-title">{{ ratingIntelligence.peerComparison.title }}</h3>
                    <p v-if="ratingIntelligence.peerComparison.description">{{ ratingIntelligence.peerComparison.description }}</p>
                </div>

                <div class="peer-card__body">
                    <div v-if="ratingIntelligence.peerComparison.metrics.length" class="peer-bars">
                        <div class="peer-legend" role="list" aria-label="Peer comparison legend">
                            <span role="listitem"><i class="peer-dot peer-dot--issuer" aria-hidden="true"></i>This issuer</span>
                            <span role="listitem"><i class="peer-dot peer-dot--median" aria-hidden="true"></i>Peer median</span>
                            <span role="listitem"><i class="peer-dot peer-dot--top" aria-hidden="true"></i>Top quartile</span>
                        </div>

                        <div v-for="metric in ratingIntelligence.peerComparison.metrics" :key="metric.label" class="peer-metric">
                            <div class="peer-metric__top">
                                <span>{{ metric.label }}</span>
                                <strong>{{ metric.value }}</strong>
                            </div>
                            <p>{{ metric.helper }}</p>
                            <div
                                class="peer-track"
                                role="img"
                                :aria-label="peerMetricAriaLabel(metric)"
                                :style="{
                                    '--issuer': `${metric.issuer}%`,
                                    '--median': `${metric.median}%`,
                                    '--top': `${metric.top}%`
                                }"
                            >
                                <VTooltip
                                    :text="`Peer median: ${metric.medianValue}`"
                                    location="top"
                                    class="peer-track-tooltip"
                                    :aria-label="`Peer median ${metric.medianValue} tooltip`"
                                >
                                    <template #activator="{ props }">
                                        <span
                                            v-bind="props"
                                            class="peer-track__marker peer-track__marker--median"
                                            :aria-label="`Peer median: ${metric.medianValue}`"
                                        ></span>
                                    </template>
                                </VTooltip>
                                <VTooltip
                                    :text="`Top quartile: ${metric.topValue}`"
                                    location="top"
                                    class="peer-track-tooltip"
                                    :aria-label="`Top quartile ${metric.topValue} tooltip`"
                                >
                                    <template #activator="{ props }">
                                        <span
                                            v-bind="props"
                                            class="peer-track__marker peer-track__marker--top"
                                            :aria-label="`Top quartile: ${metric.topValue}`"
                                        ></span>
                                    </template>
                                </VTooltip>
                                <VTooltip
                                    :text="`This issuer: ${metric.issuerValue}`"
                                    location="top"
                                    class="peer-track-tooltip"
                                    :aria-label="`This issuer ${metric.issuerValue} tooltip`"
                                >
                                    <template #activator="{ props }">
                                        <span
                                            v-bind="props"
                                            class="peer-track__marker peer-track__marker--issuer"
                                            :aria-label="`This issuer: ${metric.issuerValue}`"
                                        ></span>
                                    </template>
                                </VTooltip>
                            </div>
                        </div>
                    </div>

                    <div v-if="hasPeerSummary" class="peer-summary">
                        <h4 v-if="ratingIntelligence.peerComparison.summaryLabel" class="peer-summary__title">
                            {{ ratingIntelligence.peerComparison.summaryLabel }}
                        </h4>
                        <p v-if="ratingIntelligence.peerComparison.summary">{{ ratingIntelligence.peerComparison.summary }}</p>
                        <span
                            v-for="(tag, index) in ratingIntelligence.peerComparison.tags"
                            :key="tag"
                            class="peer-summary__chip"
                            :class="peerTagClass(index)"
                        >
                            <i aria-hidden="true"></i>
                            {{ tag }}
                        </span>
                        <small v-if="ratingIntelligence.peerComparison.subtext">{{ ratingIntelligence.peerComparison.subtext }}</small>
                    </div>
                </div>
            </article>
            </div>
        </BondV2SignupBlurGate>
    </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import Chart from 'chart.js/auto'
import { isValidCreditRating } from '@/utils/common'
import { getRatingIcon } from '@/utils/ratingIcons'
import CreditRiskMeter from '@/components/bonds/CreditRiskMeter.vue'
import BondV2SignupBlurGate from './BondV2SignupBlurGate.vue'
import { useBondV2Data } from './bondV2Context'

const isOpen = ref(true)
const timelineChartCanvas = ref<HTMLCanvasElement | null>(null)
const data = useBondV2Data()
const emit = defineEmits<{
    'signup-required': [payload: SignupGatePayload]
}>()

interface SignupGatePayload {
    title: string
    message: string
    ctaName: string
}

const ratingIntelligence = computed(() => data.value.ratingIntelligence)
const computedSecurityLabel = computed(() => {
    return data.value.tags.find((tag) => {
        const lowerTag = tag.toLowerCase()
        const isSecured = (lowerTag.includes('secured') && !lowerTag.includes('unsecured')) || lowerTag.includes('debenture')
        const isUnsecured = lowerTag.includes('unsecured') || lowerTag.includes('subordinate')
        return isSecured || isUnsecured
    }) || null
})
const ratingHistory = computed(() => (ratingIntelligence.value?.timeline?.items ?? []).slice(0, 4))
const hasRatingTimeline = computed(() => ratingHistory.value.length >= 2)
const hasPeerSummary = computed(() => {
    const peerComparison = ratingIntelligence.value?.peerComparison

    return Boolean(
        peerComparison?.summaryLabel?.trim()
        || peerComparison?.summary?.trim()
        || peerComparison?.tags?.length
        || peerComparison?.subtext?.trim()
    )
})
const hasRatingSummary = computed(() => Boolean(
    ratingIntelligence.value?.currentRating
    || ratingIntelligence.value?.rationaleSummary
    || ratingIntelligence.value?.summaryPoints
))
const currentRatingForIcon = computed(() => ratingIntelligence.value?.currentRating?.split('/')[0]?.trim() ?? '')
const currentRatingIcon = computed(() => {
    return isValidCreditRating(currentRatingForIcon.value) ? getRatingIcon(currentRatingForIcon.value) : null
})
const ratingTimelineAriaLabel = computed(() => {
    if (!ratingHistory.value.length) return 'Rating history timeline'

    return `Rating history from ${ratingHistory.value[0].year} to ${ratingHistory.value[ratingHistory.value.length - 1].year}`
})
const ratingIntelGateMessage = computed(() => `Sign up to view ${ratingIntelligence.value?.title || 'Credit Rating Intelligence'}`)
let timelineChart: Chart | null = null

const handleSignupGate = () => {
    const sectionTitle = ratingIntelligence.value?.title || 'Credit Rating Intelligence'
    emit('signup-required', {
        title: `Sign Up to View ${sectionTitle}`,
        message: `Sign up to access ${sectionTitle}, rating history, and peer comparison details.`,
        ctaName: 'signup-credit-rating-bond-details'
    })
}

const timelineToneColors = {
    orange: '#b45309',
    blue: '#2563eb',
    green: '#0f6b32',
    indigo: '#4f46e5'
}

const ratingSignalClass = (chip: string, index: number) => {
    const normalizedChip = chip.toLowerCase()

    if (normalizedChip.includes('upgrade')) return 'rating-signal--upgrade'
    if (normalizedChip.includes('stable')) return 'rating-signal--stable'

    const classes = [
        'rating-signal--indigo',
        'rating-signal--olive',
        'rating-signal--red',
        'rating-signal--rust',
        'rating-signal--plum',
        'rating-signal--amber',
        'rating-signal--blue',
        'rating-signal--teal'
    ]

    return classes[index % classes.length]
}

const peerTagClass = (index: number) => {
    const classes = [
        'peer-summary__chip--indigo',
        'peer-summary__chip--olive',
        'peer-summary__chip--red',
        'peer-summary__chip--rust',
        'peer-summary__chip--plum',
        'peer-summary__chip--amber',
        'peer-summary__chip--blue',
        'peer-summary__chip--teal'
    ]

    return classes[index % classes.length]
}

const peerMetricAriaLabel = (metric: any) => (
    `${metric.label}: this issuer ${metric.issuerValue}, peer median ${metric.medianValue}, top quartile ${metric.topValue}. ${metric.helper}`
)

const ratingLabelPlacement = (index: number, count: number) => {
    const placements = count >= 4
        ? [
            { xOffset: -14, yOffset: -38, textAlign: 'center' as CanvasTextAlign },
            { xOffset: 0, yOffset: 38, textAlign: 'center' as CanvasTextAlign },
            { xOffset: 0, yOffset: -38, textAlign: 'center' as CanvasTextAlign },
            { xOffset: -44, yOffset: 38, textAlign: 'left' as CanvasTextAlign }
        ]
        : [
            { xOffset: -16, yOffset: -38, textAlign: 'center' as CanvasTextAlign },
            { xOffset: 0, yOffset: -32, textAlign: 'center' as CanvasTextAlign },
            { xOffset: -56, yOffset: 38, textAlign: 'left' as CanvasTextAlign }
        ]

    return placements[index] ?? placements[placements.length - 1]
}

const truncateCanvasText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number) => {
    if (ctx.measureText(text).width <= maxWidth) return text

    let truncatedText = text
    while (truncatedText.length > 0 && ctx.measureText(`${truncatedText}...`).width > maxWidth) {
        truncatedText = truncatedText.slice(0, -1)
    }

    return truncatedText ? `${truncatedText}...` : text
}

const clampCanvasCenterX = (x: number, width: number, canvasWidth: number, padding = 4) => {
    const halfWidth = width / 2

    return Math.min(
        Math.max(x, halfWidth + padding),
        canvasWidth - halfWidth - padding
    )
}

const ratingHistoryLabelsPlugin = {
    id: 'ratingHistoryLabels',
    afterDatasetsDraw(chart: Chart) {
        const meta = chart.getDatasetMeta(0)
        const ctx = chart.ctx
        const count = ratingHistory.value.length

        ctx.save()
        ctx.fillStyle = '#111827'
        ctx.font = '700 14px Inter, sans-serif'
        ctx.textBaseline = 'middle'

        for (const [index, point] of meta.data.entries()) {
            const historyItem = ratingHistory.value[index]
            const ratingLabel = historyItem?.rating
            if (!ratingLabel) continue

            const { xOffset, yOffset } = ratingLabelPlacement(index, count)
            const requestedLabelX = point.x + xOffset
            const ratingY = point.y + yOffset
            const titleY = ratingY + 16
            const maxTitleWidth = 118

            ctx.font = '700 14px Inter, sans-serif'
            const ratingWidth = ctx.measureText(ratingLabel).width
            let titleLabel = ''
            let titleWidth = 0

            if (historyItem.title) {
                ctx.font = '600 10px Inter, sans-serif'
                titleLabel = truncateCanvasText(ctx, historyItem.title, maxTitleWidth)
                titleWidth = ctx.measureText(titleLabel).width
            }

            const labelWidth = Math.max(ratingWidth, titleWidth)
            const labelX = clampCanvasCenterX(requestedLabelX, labelWidth, chart.width)

            ctx.textAlign = 'center'
            ctx.fillStyle = '#111827'
            ctx.font = '700 14px Inter, sans-serif'
            ctx.fillText(ratingLabel, labelX, ratingY)

            if (titleLabel) {
                ctx.fillStyle = '#64748b'
                ctx.font = '600 10px Inter, sans-serif'
                ctx.fillText(titleLabel, labelX, titleY, maxTitleWidth)
            }
        }

        ctx.restore()
    }
}

const destroyTimelineChart = () => {
    timelineChart?.destroy()
    timelineChart = null
}

const initTimelineChart = () => {
    const canvas = timelineChartCanvas.value
    const ctx = canvas?.getContext('2d')

    if (!ctx || !hasRatingTimeline.value) return

    destroyTimelineChart()

    timelineChart = new Chart(ctx, {
        type: 'line',
        data: {
            labels: ratingHistory.value.map((item) => item.year),
            datasets: [
                {
                    data: ratingHistory.value.map((item) => item.value),
                    borderColor: '#1d16ff',
                    borderWidth: 3,
                    cubicInterpolationMode: 'monotone',
                    fill: false,
                    pointBackgroundColor: ratingHistory.value.map((item) => timelineToneColors[item.tone]),
                    pointBorderColor: ratingHistory.value.map((item) => timelineToneColors[item.tone]),
                    pointBorderWidth: 0,
                    pointRadius: 6,
                    tension: 0.45
                }
            ]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            events: [],
            layout: {
                padding: {
                    top: 46,
                    right: 24,
                    bottom: 10,
                    left: 24
                }
            },
            plugins: {
                legend: { display: false },
                datalabels: { display: false }
            } as any,
            scales: {
                x: {
                    border: { display: false },
                    grid: { display: false },
                    ticks: {
                        color: '#64748b',
                        font: {
                            family: 'Inter',
                            size: 12,
                            weight: 400
                        },
                        padding: 8
                    }
                },
                y: {
                    min: 20,
                    max: 92,
                    border: { display: false },
                    grid: { display: false },
                    ticks: { display: false }
                }
            }
        },
        plugins: [ratingHistoryLabelsPlugin]
    } as any)
}

watch([isOpen, ratingHistory], async ([open]) => {
    if (open && ratingHistory.value.length) {
        await nextTick()
        initTimelineChart()
        return
    }

    destroyTimelineChart()
}, { immediate: true })

onBeforeUnmount(destroyTimelineChart)
</script>

<style scoped>
.rating-intel {
    border: none;
    border-radius: var(--bond-v2-radius, 7px);
    background: #F9FBFF;
    overflow: hidden;
    padding: 12px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
}

.rating-intel__header {
    width: 100%;
    border: none;
    background: #F9FBFF;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 0;
    cursor: pointer;
}

.rating-intel__heading {
    margin: 0;
}

.rating-intel__title {
    margin: 0;
    color: var(--bond-v2-text, var(--color-text-primary));
    font-size: 15px;
    font-weight: 800;
}

.rating-intel__header i {
    color: var(--bond-v2-primary, var(--color-primary-40));
    font-size: 18px;
}

.rating-intel__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-top: 16px;
}

.rating-summary-container {
    width: 100%;
}
.rating-crom-section {
    width: 100%;
}
.w-100 {
    width: 100%;
}
.mb-6 {
    margin-bottom: 24px;
}
.mt-4 {
    margin-top: 16px;
}

.rating-intel__description {
    margin: 0;
    color: #5b6684;
    font-family: Inter, var(--font-family-primary);
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
}

.rating-signals,
.peer-filters,
.peer-legend,
.timeline-notes,
.peer-card__body {
    display: flex;
}

.rating-signals {
    flex-wrap: wrap;
    gap: 10px;
}

.rating-signal,
.peer-filters button {
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    background: #f8fafc;
    color: #64748b;
    font-family: Inter, var(--font-family-primary);
    font-size: 12px;
    font-weight: 500;
    line-height: 15px;
    padding: 10px 14px;
    white-space: nowrap;
}

.rating-signal--upgrade {
    border-color: #2563eb;
    background: #eff6ff;
    color: #2563eb;
    font-weight: 600;
}

.rating-signal--stable {
    border-color: #0f6b32;
    background: #f0fdf4;
    color: #0f6b32;
    font-weight: 600;
}

.rating-signal--blue {
    border-color: #bfdbfe;
    background: #eff6ff;
    color: #0027af;
    font-weight: 600;
}

.rating-signal--teal {
    border-color: #99f6e4;
    background: #ecfdf5;
    color: #0f766e;
    font-weight: 600;
}

.rating-signal--amber {
    border-color: #fde68a;
    background: #fffbeb;
    color: #b45309;
    font-weight: 600;
}

.rating-signal--plum {
    border-color: #fbcfe8;
    background: #fdf2f8;
    color: #8f2a3a;
    font-weight: 600;
}

.rating-signal--olive {
    border-color: #d9f99d;
    background: #f7fee7;
    color: #6b7d00;
    font-weight: 600;
}

.rating-signal--red {
    border-color: #fecaca;
    background: #fef2f2;
    color: #a31616;
    font-weight: 600;
}

.rating-signal--indigo {
    border-color: #c7d2fe;
    background: #eef2ff;
    color: #4f67e8;
    font-weight: 600;
}

.rating-signal--rust {
    border-color: #fed7aa;
    background: #fff7ed;
    color: #9a3412;
    font-weight: 600;
}

.rating-summary {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 16px;
}

.rating-summary > :only-child {
    grid-column: 1 / -1;
}

.rating-card,
.timeline-card,
.outlook-card,
.drivers-card,
.peer-card {
    border-radius: 8px;
    background: #fff;
}

.rating-card {
    min-height: 250px;
}

.rating-card--current {
    background: #eff6ff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 24px;
}

.rating-card__rating-icon {
    width: min(228px, 100%);
    height: 126px;
    display: block;
    object-fit: contain;
}

.rating-card--current strong {
    color: #111827;
    font-family: Inter, var(--font-family-primary);
    font-size: 42px;
    font-weight: 700;
    line-height: 48px;
    text-align: center;
    white-space: nowrap;
}

.rating-card--current span {
    color: #64748b;
    font-family: Inter, var(--font-family-primary);
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
}

.rating-card--rationale {
    padding: 22px;
}

.rating-card__title,
.section-title {
    margin: 0;
    color: #1b2559;
    font-family: Inter, var(--font-family-primary);
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
}

.rating-card--rationale .rating-card__title {
    color: #111827;
    line-height: 26px;
}

.rating-card--rationale p,
.rating-bullets li,
.outlook-card p,
.outlook-card__bullets li,
.driver-card p,
.section-copy p,
.peer-summary p,
.peer-summary small {
    margin: 0;
    font-family: Inter, var(--font-family-primary);
}

.rating-card--rationale p {
    margin-top: 14px;
    color: #1b2559;
    font-size: 11px;
    font-weight: 400;
    line-height: 17px;
}

.rating-card__eyebrow {
    margin-top: 14px;
    color: #5b6684;
    font-family: Inter, var(--font-family-primary);
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
}

.rating-bullets {
    margin: 8px 0 0;
    padding-left: 18px;
}

.rating-bullets li {
    margin: 0;
    color: #1b2559;
    font-size: 11px;
    font-weight: 400;
    line-height: 20px;
}

.timeline-card {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 16px 12px;
}

.section-copy {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.section-copy p {
    color: #64748b;
    font-size: 13px;
    font-weight: 400;
    line-height: 22px;
}

.timeline-chart {
    position: relative;
    height: 294px;
    margin-top: 2px;
    overflow: hidden;
}

.timeline-chart canvas {
    width: 100% !important;
    height: 100% !important;
    display: block;
}

.timeline-notes {
    gap: 14px;
}

.timeline-note {
    flex: 1 1 0;
    min-height: 86px;
    border-radius: 6px;
    padding: 12px;
}

.timeline-note strong,
.timeline-note p {
    margin: 0;
    font-family: Inter, var(--font-family-primary);
}

.timeline-note strong {
    display: block;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
}

.timeline-note p {
    margin-top: 8px;
    color: #1b2559;
    font-size: 11px;
    font-weight: 400;
    line-height: 18px;
}

.timeline-note--orange {
    border: 1px solid #9a3412;
    background: #fff7ed;
}

.timeline-note--orange strong {
    color: #9a3412;
}

.timeline-note--blue {
    border: 1px solid #2563eb;
    background: #eff6ff;
}

.timeline-note--blue strong {
    color: #2563eb;
}

.timeline-note--green {
    border: 1px solid #0f6b32;
    background: #f0fdf4;
}

.timeline-note--green strong {
    color: #0f6b32;
}

.timeline-note--indigo {
    border: 1px solid #4f46e5;
    background: #eef2ff;
}

.timeline-note--indigo strong {
    color: #4f46e5;
}

.outlook-card,
.drivers-card,
.peer-card {
    border-radius: 12px;
    padding: 16px 12px;
}

.outlook-card {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.outlook-card p {
    color: #1b2559;
    font-size: 13px;
    font-weight: 400;
    line-height: 19px;
}

.outlook-card__label {
    color: #5b6684;
    font-family: Inter, var(--font-family-primary);
    font-size: 15px;
    font-weight: 600;
    line-height: 16px;
}

.outlook-card__bullets {
    margin: 0;
    padding-left: 18px;
}

.outlook-card__bullets li {
    color: #1b2559;
    font-family: Inter, var(--font-family-primary);
    font-size: 13px;
    font-weight: 400;
    line-height: 20px;
}

.outlook-card strong {
    color: #b91c1c;
    font-family: Inter, var(--font-family-primary);
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
}

.drivers-card {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.drivers-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px;
}

.driver-card {
    min-height: 109px;
    border-radius: 6px;
    background: #f9fbff;
    padding: 16px;
}

.driver-card__header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 8px;
}

.driver-card__title {
    min-width: 0;
    margin: 0;
    color: #111827;
    font-family: Inter, var(--font-family-primary);
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
}

.driver-card__status {
    display: inline-flex;
    flex: 0 0 auto;
    border-radius: 999px;
    padding: 6px 10px;
    font-family: Inter, var(--font-family-primary);
    font-size: 11px;
    font-weight: 600;
    line-height: 13px;
}

.driver-card__status--green {
    background: #f0fdf4;
    color: #0f6b32;
}

.driver-card__status--blue {
    background: #eff6ff;
    color: #2563eb;
}

.driver-card__status--amber {
    background: #fffbeb;
    color: #92400e;
}

.driver-card__status--indigo {
    background: #eef2ff;
    color: #4f46e5;
}

.driver-card__status--red {
    background: #fef2f2;
    color: #b91c1c;
}

.driver-card p {
    margin: 8px 0 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
}

.peer-card {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.peer-filters {
    flex-wrap: wrap;
    gap: 8px;
}

.peer-filters button {
    background: #fff;
    cursor: pointer;
    font-weight: 600;
    line-height: 16px;
    padding: 8px 12px;
}

.peer-filters button.active {
    border-color: #2563eb;
    background: #eff6ff;
    color: #2563eb;
}

.peer-card__body {
    gap: 14px;
    align-items: stretch;
}

.peer-bars {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.peer-legend {
    flex-wrap: wrap;
    gap: 18px;
}

.peer-legend span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #64748b;
    font-family: Inter, var(--font-family-primary);
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
    white-space: nowrap;
}

.peer-dot,
.peer-summary__chip i {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
    flex: 0 0 8px;
}

.peer-dot--issuer {
    background: #2563eb;
}

.peer-dot--median {
    background: #64748b;
}

.peer-dot--top {
    background: #0f6b32;
}

.peer-metric {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.peer-metric__top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    color: #111827;
    font-family: Inter, var(--font-family-primary);
    font-size: 14px;
    line-height: 18px;
}

.peer-metric__top span {
    font-weight: 600;
}

.peer-metric__top strong {
    font-weight: 700;
}

.peer-metric p {
    margin: 0;
    color: #64748b;
    font-family: Inter, var(--font-family-primary);
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
}

.peer-track {
    position: relative;
    height: 16px;
    border-radius: 999px;
    background: #f1f5f9;
    overflow: visible;
}

.peer-track__marker {
    position: absolute;
    top: 0;
    transform: translateX(-50%);
    z-index: 1;
    border: 0;
    padding: 0;
    cursor: pointer;
    display: block;
}

.peer-track__marker:focus-visible {
    outline: 2px solid #0027af;
    outline-offset: 3px;
}

.peer-track__marker--issuer {
    left: var(--issuer);
    top: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #2563eb;
}

.peer-track__marker--median {
    left: var(--median);
    width: 4px;
    height: 16px;
    border-radius: 2px;
    background: #64748b;
}

.peer-track__marker--top {
    left: var(--top);
    width: 3px;
    height: 16px;
    border-radius: 2px;
    background: #0f6b32;
}

.peer-summary {
    width: 320px;
    min-height: 520px;
    border-radius: 8px;
    background: #eff6ff;
    padding: 18px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
}

.peer-summary__title {
    margin: 0;
    color: #0027af;
    font-family: Inter, var(--font-family-primary);
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
}

.peer-summary p {
    color: #0027af;
    font-size: 13px;
    font-weight: 400;
    line-height: 20px;
}

.peer-summary__chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border-radius: 999px;
    padding: 8px 10px;
    font-family: Inter, var(--font-family-primary);
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    white-space: nowrap;
}

.peer-summary__chip--blue {
    background: #eff6ff;
    color: #0027af;
}

.peer-summary__chip--blue i {
    background: #0027af;
}

.peer-summary__chip--green {
    background: #f0fdf4;
    color: #0f6b32;
}

.peer-summary__chip--green i {
    background: #0f6b32;
}

.peer-summary__chip--amber {
    background: #fffbeb;
    color: #b45309;
}

.peer-summary__chip--amber i {
    background: #b45309;
}

.peer-summary__chip--indigo {
    background: #eef2ff;
    color: #4f67e8;
}

.peer-summary__chip--indigo i {
    background: #4f67e8;
}

.peer-summary__chip--plum {
    background: #fdf2f8;
    color: #8f2a3a;
}

.peer-summary__chip--plum i {
    background: #8f2a3a;
}

.peer-summary__chip--olive {
    background: #f7fee7;
    color: #6b7d00;
}

.peer-summary__chip--olive i {
    background: #6b7d00;
}

.peer-summary__chip--red {
    background: #fef2f2;
    color: #a31616;
}

.peer-summary__chip--red i {
    background: #a31616;
}

.peer-summary__chip--rust {
    background: #fff7ed;
    color: #9a3412;
}

.peer-summary__chip--rust i {
    background: #9a3412;
}

.peer-summary__chip--teal {
    background: #ecfdf5;
    color: #0f766e;
}

.peer-summary__chip--teal i {
    background: #0f766e;
}

.peer-summary small {
    color: #64748b;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
}

@media (max-width: 900px) {
    .rating-summary,
    .drivers-grid {
        grid-template-columns: 1fr;
    }

    .peer-card__body {
        flex-direction: column;
    }

    .peer-summary {
        width: auto;
        min-height: 0;
    }
}

@media (max-width: 560px) {
    .rating-intel {
        border-radius: 0;
        padding: 12px 16px;
        box-sizing: border-box;
    }

    .rating-intel__title {
        font-size: 14px;
    }

    .rating-intel__description,
    .section-copy p,
    .outlook-card p {
        font-size: 12px;
        line-height: 20px;
    }

    .rating-signals,
    .peer-filters {
        flex-wrap: nowrap;
        overflow-x: auto;
        scrollbar-width: none;
    }

    .rating-signals::-webkit-scrollbar,
    .peer-filters::-webkit-scrollbar {
        display: none;
    }

    .rating-signal,
    .peer-filters button {
        flex: 0 0 auto;
        padding: 8px 10px;
        font-size: 11px;
    }

    .rating-card {
        min-height: 0;
    }

    .rating-card--current {
        padding: 20px 12px;
    }

    .rating-card__rating-icon {
        width: min(176px, 100%);
        height: 98px;
    }

    .rating-card--current strong {
        font-size: 28px;
        line-height: 34px;
        white-space: normal;
    }

    .rating-card--rationale {
        padding: 16px 12px;
    }

    .timeline-chart {
        height: 220px;
    }

    .timeline-notes {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
    }

    .timeline-note {
        flex: 0 0 auto;
        width: 100%;
        min-height: 0;
        box-sizing: border-box;
        overflow: visible;
    }

    .drivers-card,
    .outlook-card,
    .peer-card,
    .timeline-card {
        padding: 14px 10px;
    }

    .driver-card {
        min-height: 0;
        padding: 14px 12px;
    }

    .peer-legend {
        gap: 10px;
    }

    .peer-legend span,
    .peer-summary__chip {
        white-space: normal;
    }

    .peer-metric__top {
        font-size: 12px;
        line-height: 16px;
    }

    .peer-summary {
        padding: 14px 12px;
    }

    .peer-summary__title {
        font-size: 15px;
        line-height: 20px;
    }
}
</style>

<style>
.peer-track-tooltip .v-overlay__content,
.peer-track-tooltip.v-tooltip .v-overlay__content {
    font-size: 13.5px !important;
    line-height: 17px !important;
    padding: 5px 8px !important;
}
</style>
